import { useEffect, useState } from 'react';
import { epubjsService, type EpubJsManifestDto } from '../../api/epubjsService';

interface UseEpubJsManifestResult {
  manifest: EpubJsManifestDto | null;
  error: Error | null;
  isLoading: boolean;
}

/**
 * Fetches the Phase 1b /epubjs/manifest/{bookId} response once per bookId.
 * Consumers (UnifiedEpubRenderer) must wait for `manifest !== null` before
 * constructing the epub.js Book — the custom archive loader needs it to
 * synthesize content.opf.
 */
export function useEpubJsManifest(bookId: string | undefined): UseEpubJsManifestResult {
  const [manifest, setManifest] = useState<EpubJsManifestDto | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!bookId) return;
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    epubjsService
      .getManifest(bookId)
      .then((m) => {
        if (!cancelled) setManifest(m);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e : new Error(String(e)));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [bookId]);

  return { manifest, error, isLoading };
}
